import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Follow-up Reason master (Masters → Followup Reasons). Operator-curated
 * catalogue of reasons that drive a follow-up (e.g. "Customer requested
 * callback", "Quote needs revision", "Awaiting documents"). Shape mirrors
 * Department / TripType (ADR-0016 / ADR-0020).
 *
 * Ships standalone: there is no FK from `lead_follow_ups` yet. Wiring the
 * column onto LeadFollowUp + the follow-up form will land in a follow-up
 * migration so the catalogue can be curated independently.
 */

export const FollowupReasonListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type FollowupReasonListItem = z.infer<typeof FollowupReasonListItemSchema>;

export const FollowupReasonListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type FollowupReasonListQuery = z.infer<typeof FollowupReasonListQuerySchema>;

export const FollowupReasonListResponseSchema = paginatedResponseSchema(
  FollowupReasonListItemSchema,
);
export type FollowupReasonListResponse = z.infer<typeof FollowupReasonListResponseSchema>;

export const FollowupReasonDetailSchema = FollowupReasonListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type FollowupReasonDetail = z.infer<typeof FollowupReasonDetailSchema>;

const FollowupReasonCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateFollowupReasonRequestSchema = FollowupReasonCoreFieldsSchema;
export type CreateFollowupReasonRequest = z.infer<typeof CreateFollowupReasonRequestSchema>;

export const UpdateFollowupReasonRequestSchema = FollowupReasonCoreFieldsSchema.partial();
export type UpdateFollowupReasonRequest = z.infer<typeof UpdateFollowupReasonRequestSchema>;

// Small ref shape used inline when the follow-up form / detail
// embeds a reason next to other refs.
export const FollowupReasonRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
});
export type FollowupReasonRef = z.infer<typeof FollowupReasonRefSchema>;
